import { get, groupBy, isEmpty } from 'lodash';
import {
  add,
  isBefore,
  differenceInDays,
  format,
  sub,
  startOfDay,
  getMonth,
} from 'date-fns';

// slices
import { IPassengerSearchForm } from '@/slices/passenger/slices/type.d';
import { IFlightResultItem } from '@/containers/SearchFlightTab/SearchFlightResultItem/index.d';

// utils
import { getAreaPointArr } from '@/utils';
import AIRLINES_VN from '@/mappings/airlines_vn.json';
import { calendarVN } from '@/utils/calendarVietNam';

import {
  TFlightCountSeoResponse,
  IGroupedByFlightNameSeoRoute,
  IItemMinFare,
} from './SectionManager/type';
import { FlightData } from '@/slices/flightData/slices';
import { TGenerateFlightDataRequest } from '@/utils/generateFlightRequest';

export const generateDays = ({
  date,
  minDate,
  maxDate,
  dayLimit,
}: {
  date: string;
  minDate?: string | null;
  maxDate?: string | null;
  dayLimit?: number;
}) => {
  const selectedDate = new Date(date);
  const today = new Date();
  let startDate: Date = today;
  const checkingMinDate = minDate ? new Date(minDate) : new Date();
  let checkingMaxDate = maxDate ? new Date(maxDate) : new Date();
  if (getMonth(checkingMinDate) === getMonth(checkingMaxDate)) {
    checkingMaxDate = add(checkingMaxDate, { months: 1 });
  } else {
    checkingMaxDate = add(checkingMaxDate, { days: 15 });
  }

  const farthestPastDate = sub(selectedDate, { days: 15 });
  if (isBefore(today, farthestPastDate)) startDate = farthestPastDate;
  if (minDate) startDate = checkingMinDate;

  const flightDayLimit = maxDate
    ? differenceInDays(
      startOfDay(new Date(checkingMaxDate)),
      startOfDay(startDate),
    ) + 1
    : parseInt(process.env.NEXT_PUBLIC_FLIGHT_CALENDAR_DAY_LIMIT || '30', 10);

  let checkingDate = sub(startDate, { days: 1 });

  const days = new Array(
    dayLimit || (flightDayLimit < 0 ? -flightDayLimit : flightDayLimit) || 0,
  )
    .fill({})
    .map(() => {
      checkingDate = add(checkingDate, { days: 1 });
      const year = checkingDate.getFullYear();
      const month = checkingDate.getMonth() + 1;
      const dateNo = checkingDate.getDate();
      const dayVN = calendarVN.fromGregorian(year, month, dateNo) as any;
      return {
        date: checkingDate,
        dateStr: format(new Date(checkingDate), 'yyyy-MM-dd'),
        minFare: '',
        id: '',
        dayVN: dayVN.day,
        monthVN: dayVN.month,
        airline: {
          name: '',
        },
      };
    });

  return days;
};

export const groupItemByDate = (data: TFlightCountSeoResponse) => {
  if (!data) return null;
  const temp: any = [];
  const groupedByFlightNameSeoRoute: IGroupedByFlightNameSeoRoute = data?.grouped_by_flight_name_seo_route;
  if (!groupedByFlightNameSeoRoute) {
    return {};
  }

  Object.keys(groupedByFlightNameSeoRoute).forEach((routeCode) => {
    const itemObj = groupedByFlightNameSeoRoute[routeCode];
    const AIRLINES: { [key: string]: any } = AIRLINES_VN;
    if (itemObj) {
      Object.keys(itemObj?.min_flight_by_airline)
        .filter((key) => !!key && key !== 'BL')
        .map((key: string) => {
          const airlineMarkup = `${key}_VXR_MARKUP`;
          const value = itemObj?.min_flight_by_airline[key]?.map(
            (item: IItemMinFare) => ({
              ...item,
              total_price_adult:
                item?.final_min_fare || item?.total_price_adult,
              airline: AIRLINES[key as string] || '',
            }),
          );
          temp.push(...value);
          return true;
        });
    }
  });
  return groupBy(temp, 'pickup_date');
};

export const getLowestPriceSeoRoute = (dataStatisticsSeo: any) => {
  let lowestPriceRoute = null as any;
  if (get(dataStatisticsSeo, 'grouped_by_flight_name_seo_route')) {
    const groupedByFlightNameSeoRoute = dataStatisticsSeo.grouped_by_flight_name_seo_route;
    Object.keys(groupedByFlightNameSeoRoute).forEach((routeName) => {
      const route = groupedByFlightNameSeoRoute[routeName];
      if (!lowestPriceRoute) {
        lowestPriceRoute = route;
      } else if (
        route.lowest_price
        && route.lowest_price < lowestPriceRoute.lowest_price
      ) {
        lowestPriceRoute = route;
      }
    });
  }
  return lowestPriceRoute || {};
};

export const groupFlightByDate = (data: FlightData[]) => {
  if (isEmpty(data)) return null;
  const AIRLINES: { [key: string]: any } = AIRLINES_VN;
  const temp = data.map((flightData) => {
    const value = {
      pickup_date: flightData.date,
      pickup_time: flightData.time,
      pickup_time_number: Number(flightData.time.replace(':', '')),
      total_price_adult:
        get(flightData, 'route.schedules[0].totalPriceAdult') || 0,
      id: flightData.idIndex,
      dropoff_time: flightData.arrivalTime,
      airline: AIRLINES[flightData.airline] || null,
    };
    return value;
  });
  return groupBy(temp, 'pickup_date');
};

export const generateMinFareRequestSeo = ({
  fromDateStr,
  toDateStr,
  startPoint,
  toPoint,
  passenger,
  airline,
  is_count_tet_price,
  month_years,
}: {
  fromDateStr: string;
  toDateStr: string;
  startPoint: IFlightResultItem;
  toPoint: IFlightResultItem;
  passenger: IPassengerSearchForm;
  airline?: string;
  is_count_tet_price?: number;
  month_years?: string[];
}) => ({
  from: getAreaPointArr(startPoint),
  to: getAreaPointArr(toPoint),
  date_range: {
    min: fromDateStr,
    max: toDateStr,
  },
  month_years,
  is_count_tet_price,
  quantity: passenger.adt + passenger.chd,
  airline: [airline || ''],
});

export const generateFlightRequestSeo = ({
  fromDateStr,
  startPoint,
  toPoint,
  passenger,
  airline,
}: {
  fromDateStr: string;
  startPoint: IFlightResultItem;
  toPoint: IFlightResultItem;
  passenger: IPassengerSearchForm;
  airline?: any;
}): TGenerateFlightDataRequest => ({
  from: getAreaPointArr(startPoint),
  to: getAreaPointArr(toPoint),
  date: fromDateStr,
  quantity: passenger.adt + passenger.chd,
  airline: [airline || ''],
  page: 1,
  pagesize: 10,
  flight_class: 'PT',
});
